export interface IpCountryResult {
    country: string;
    countryCode: string;
}

export interface IpGeolocationInfo extends IpCountryResult {
    city: string | null;
    lat: number;
    lon: number;
}

const PRIVATE_IP_PATTERNS = [
    /^127\./,
    /^10\./,
    /^192\.168\./,
    /^172\.(1[6-9]|2\d|3[0-1])\./,
    /^::1$/,
    /^fc00:/i,
    /^fe80:/i,
];

function isPrivateOrLocalIp(ip: string): boolean {
    return PRIVATE_IP_PATTERNS.some((pattern) => pattern.test(ip));
}

function isValidIpAddress(ip: string): boolean {
    if (!ip || isPrivateOrLocalIp(ip)) return false;

    const ipv4 = /^(\d{1,3}\.){3}\d{1,3}$/;
    const ipv6 = /^[0-9a-f:]+$/i;
    return ipv4.test(ip) || ipv6.test(ip);
}

function isValidCoordinate(lat: number, lon: number): boolean {
    return (
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        lat >= -90 &&
        lat <= 90 &&
        lon >= -180 &&
        lon <= 180
    );
}

export function buildIpWhoIsUrl(clientIp?: string | null): string {
    if (clientIp && isValidIpAddress(clientIp)) {
        return `https://ipwho.is/${encodeURIComponent(clientIp)}`;
    }
    return "https://ipwho.is/";
}

export function parseIpWhoIsCountry(data: unknown): IpCountryResult | null {
    const info = parseIpGeolocationInfo(data);
    if (!info) return null;
    return { country: info.country, countryCode: info.countryCode };
}

/**
 * Parse IP geolocation used for country detection and approximate fallback city.
 */
export function parseIpGeolocationInfo(data: unknown): IpGeolocationInfo | null {
    if (!data || typeof data !== "object") return null;

    const record = data as Record<string, unknown>;
    if (record.success !== true) return null;

    const country = typeof record.country === "string" ? record.country.trim() : "";
    const countryCode =
        typeof record.country_code === "string"
            ? record.country_code.trim().toLowerCase()
            : "";
    const city = typeof record.city === "string" ? record.city.trim() : null;

    const lat = typeof record.latitude === "number" ? record.latitude : Number(record.latitude);
    const lon = typeof record.longitude === "number" ? record.longitude : Number(record.longitude);

    if (!country || !countryCode || !isValidCoordinate(lat, lon)) return null;

    return {
        country,
        countryCode,
        city: city || null,
        lat: parseFloat(lat.toFixed(3)),
        lon: parseFloat(lon.toFixed(3)),
    };
}

/**
 * Look up the visitor's approximate location from their IP.
 * Calls ipwho.is directly from the browser (CORS-enabled); the provider
 * geolocates the caller's own IP, so no server hop is needed.
 */
export async function fetchIpGeolocationInfo(): Promise<IpGeolocationInfo | null> {
    try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 8000);

        const response = await fetch(buildIpWhoIsUrl(), {
            signal: controller.signal,
            headers: { Accept: "application/json" },
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
            return null;
        }

        const data: unknown = await response.json();
        return parseIpGeolocationInfo(data);
    } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
            console.warn("IP geolocation lookup timed out");
        } else {
            console.error("Error fetching IP geolocation:", error);
        }
        return null;
    }
}
